import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
} from '@angular/core';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';

import { ThemeService } from '../../../services/theme.service';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

export interface HubChartDataset {
  label: string;
  data: number[];
  /** A CSS colour, `var(--c-…)` reference or bare `--c-…` token name (resolved at render time, theme-aware). */
  color: string;
}

/** Resolve a `var(--x)` / `--x` reference against the document's current theme. */
function resolveColor(value: string, fallback = ''): string {
  const match = /^\s*(?:var\(\s*)?(--[\w-]+)/.exec(value);
  if (!match) {
    return value || fallback;
  }
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim();
  return resolved || fallback;
}

/** Grouped bar chart (Chart.js) used by the hub overview. */
@Component({
  selector: 'app-hub-bar-chart',
  standalone: true,
  template: `<canvas #canvas class="w-full!" role="img" [attr.aria-label]="ariaLabel()"></canvas>`,
  host: { class: 'relative block h-[260px]' },
})
export class HubBarChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly labels = input<string[]>([]);
  readonly datasets = input<HubChartDataset[]>([]);
  readonly ariaLabel = input('Chart');
  readonly stacked = input(false);

  @ViewChild('canvas') private canvas?: ElementRef<HTMLCanvasElement>;
  private chart: Chart<'bar'> | null = null;
  private readonly theme = inject(ThemeService);
  private viewReady = false;

  constructor() {
    effect(() => {
      this.theme.resolved();
      if (this.viewReady) {
        // The theme class is applied by ThemeService's own effect; wait a frame for CSS vars to update.
        requestAnimationFrame(() => this.render());
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  ngOnChanges(): void {
    this.render();
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private render(): void {
    if (!this.canvas) {
      return;
    }
    this.chart?.destroy();
    const fg = resolveColor('--c-muted', 'gray');
    const grid = resolveColor('--c-border', 'lightgray');
    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'bar',
      data: {
        labels: this.labels(),
        datasets: this.datasets().map((set) => ({
          label: set.label,
          data: set.data,
          backgroundColor: resolveColor(set.color, grid),
          borderRadius: 3,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: this.stacked(),
            ticks: { maxRotation: 0, autoSkip: true, color: fg },
            grid: { color: grid },
            border: { color: grid },
          },
          y: {
            stacked: this.stacked(),
            beginAtZero: true,
            ticks: { precision: 0, color: fg },
            grid: { color: grid },
            border: { color: grid },
          },
        },
        plugins: { legend: { position: 'bottom', labels: { color: resolveColor('--c-fg', 'black') } } },
      },
    });
  }
}
